import { useState } from "react";
import { Loader2 } from "lucide-react";

import { PASSWORD_MIN } from "../hooks/useAuth";
import {
  errorTextStyle,
  inputStyle,
  loginCardStyle,
  loginTitleStyle,
  mutedTextStyle,
  pageCenterStyle,
  primaryButtonStyle,
  TOKENS as T,
} from "../styles/styles";

export default function AuthScreen({
  email,
  password,
  confirmPassword,
  error,
  busy,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onLogin,
  onSignup,
  onModeChange,
}) {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const isSignup = mode === "signup";

  function switchTo(nextMode) {
    if (busy || nextMode === mode) return;

    setMode(nextMode);
    onModeChange?.();
  }

  function submit() {
    if (isSignup) {
      onSignup();
    } else {
      onLogin();
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Enter") {
      submit();
    }
  }

  return (
    <div style={pageCenterStyle}>
      <div style={loginCardStyle}>
        <div style={markStyle}>
          <svg viewBox="0 0 512 512" width="26" height="26" aria-hidden="true">
            <path
              d="M212 178 L338 256 L212 334 Z"
              fill="#FFFFFF"
              stroke="#FFFFFF"
              strokeWidth="40"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          </svg>
        </div>

        <h1 style={loginTitleStyle}>
          {isSignup ? "Create your account" : "Welcome back"}
        </h1>
        <p style={mutedTextStyle}>
          {isSignup
            ? "Track what you love. Together."
            : "Pick up where your group left off."}
        </p>

        {/* Mode switch */}
        <div style={tabsStyle}>
          {[
            { key: "login", label: "Log in" },
            { key: "signup", label: "Create account" },
          ].map((tab) => {
            const active = mode === tab.key;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => switchTo(tab.key)}
                style={{
                  ...tabStyle,
                  background: active ? T.card : "transparent",
                  color: active ? T.foreground : T.mutedForeground,
                  boxShadow: active ? "0 1px 6px rgba(0,0,0,0.06)" : "none",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <input
          type="email"
          autoComplete="email"
          placeholder="Email"
          value={email}
          disabled={busy}
          onChange={(event) => onEmailChange(event.target.value)}
          onKeyDown={handleKeyDown}
          style={{ ...inputStyle, marginTop: 16 }}
        />

        <input
          type="password"
          autoComplete={isSignup ? "new-password" : "current-password"}
          placeholder={isSignup ? `Password (${PASSWORD_MIN}+ characters)` : "Password"}
          value={password}
          disabled={busy}
          onChange={(event) => onPasswordChange(event.target.value)}
          onKeyDown={handleKeyDown}
          style={{ ...inputStyle, marginTop: 10 }}
        />

        {isSignup && (
          <input
            type="password"
            autoComplete="new-password"
            placeholder="Confirm password"
            value={confirmPassword}
            disabled={busy}
            onChange={(event) => onConfirmPasswordChange(event.target.value)}
            onKeyDown={handleKeyDown}
            style={{ ...inputStyle, marginTop: 10 }}
          />
        )}

        {error && <p style={errorTextStyle}>{error}</p>}

        <button
          type="button"
          onClick={submit}
          disabled={busy}
          style={{
            ...primaryButtonStyle,
            opacity: busy ? 0.6 : 1,
            cursor: busy ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {busy && <Loader2 size={16} className="spin" />}
          {busy
            ? isSignup
              ? "Creating your account…"
              : "Signing you in…"
            : isSignup
              ? "Create account"
              : "Log in"}
        </button>
      </div>
    </div>
  );
}

const markStyle = {
  width: 48,
  height: 48,
  borderRadius: 16,
  background: T.primary,
  boxShadow: "0 8px 20px rgba(0,119,255,0.28)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: 16,
};

const tabsStyle = {
  display: "flex",
  gap: 4,
  marginTop: 20,
  padding: 4,
  borderRadius: 14,
  background: T.muted,
};

const tabStyle = {
  flex: 1,
  minHeight: 40,
  border: "none",
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 700,
  fontFamily: "inherit",
  cursor: "pointer",
  transition: "all 0.18s ease",
};
